import { Injectable, TemplateRef, signal } from '@angular/core';

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

export interface ToastMessage {
  severity?: ToastSeverity | string;
  summary?: string;
  detail?: string;
  /** Auto-dismiss delay in ms. Ignored when `sticky` is true. */
  life?: number;
  sticky?: boolean;
  /** Routes the toast to the template registered for this key (see ToastTemplateDirective). */
  key?: string;
  /** `false` hides the close button. Defaults to true. */
  closable?: boolean;
  /** Extra class(es) put on the toast card, for per-feature styling. */
  styleClass?: string;
  data?: any;
  [key: string]: any;
}

/** A toast currently on screen. `id` is what dismiss/close work off. */
export interface ActiveToast extends ToastMessage {
  id: number;
  severity: ToastSeverity;
  /** Set for the exit animation; the toast is removed once it finishes. */
  leaving?: boolean;
}

/**
 * The narrowest thing a helper needs in order to raise a toast.
 *
 * Both ToastService and NotificationService satisfy this. Helpers should depend on
 * this rather than on either class, since both carry private state and so are not
 * assignable to one another.
 */
export interface ToastSink {
  add(toast: ToastMessage): void;
}

/** Keeps the newest N toasts on screen; older ones are dropped rather than stacked off-viewport. */
const MAX_VISIBLE_TOASTS = 5;
/** Must match $cw-toast-exit-duration in toast-container.component.scss. */
const EXIT_ANIMATION_MS = 200;
const DEFAULT_LIFE_MS = 3000;

/**
 * Toast store — the replacement for PrimeNG's MessageService.
 *
 * The service owns the message list; ToastContainerComponent (mounted once in
 * app.component.html) renders it top-right, stacked, exactly as `<p-toast>` did.
 * MatSnackBar is deliberately not used: it shows one message at a time in a bottom
 * bar, which loses both the stacking and the per-severity card UI this app relies on.
 *
 * This is deliberately a leaf: it depends on nothing in the app. Services that
 * NotificationService itself depends on (AuthService, for one) need to raise toasts
 * without pulling NotificationService back in, which would be a DI cycle.
 * NotificationService delegates its own toast methods here.
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly toastsSignal = signal<ActiveToast[]>([]);
  private readonly templatesSignal = signal<ReadonlyMap<string, TemplateRef<unknown>>>(new Map());

  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private nextId = 1;

  /** Live toast list, oldest first. Read by ToastContainerComponent. */
  readonly toasts = this.toastsSignal.asReadonly();

  /** PrimeNG-compatible entry point. A `sticky` toast does not auto-dismiss. */
  add(toast: ToastMessage): void {
    const severity = this.normalizeSeverity(toast.severity);
    const id = this.nextId++;
    const active: ActiveToast = { ...toast, id, severity };

    const next = [...this.toastsSignal(), active];
    // Anything pushed past the visible limit is dropped, timer and all.
    for (const dropped of next.slice(0, Math.max(0, next.length - MAX_VISIBLE_TOASTS))) {
      this.clearTimer(dropped.id);
    }
    this.toastsSignal.set(next.slice(-MAX_VISIBLE_TOASTS));

    const life = typeof toast.life === 'number' ? toast.life : DEFAULT_LIFE_MS;
    if (!toast.sticky && life > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), life)
      );
    }
  }

  showSuccess(summary: string, detail?: string, life = DEFAULT_LIFE_MS): void {
    this.add({ severity: 'success', summary, detail, life });
  }

  showInfo(summary: string, detail?: string, life = DEFAULT_LIFE_MS): void {
    this.add({ severity: 'info', summary, detail, life });
  }

  showWarn(summary: string, detail?: string, life = DEFAULT_LIFE_MS): void {
    this.add({ severity: 'warn', summary, detail, life });
  }

  showError(summary: string, detail?: string, life = DEFAULT_LIFE_MS): void {
    this.add({ severity: 'error', summary, detail, life });
  }

  /** Dismiss one toast (close button, or its life running out). */
  dismiss(id: number): void {
    this.clearTimer(id);
    const exists = this.toastsSignal().some(toast => toast.id === id);
    if (!exists) {
      return;
    }
    this.toastsSignal.update(toasts =>
      toasts.map(toast => (toast.id === id ? { ...toast, leaving: true } : toast))
    );
    setTimeout(() => {
      this.toastsSignal.update(toasts => toasts.filter(toast => toast.id !== id));
    }, EXIT_ANIMATION_MS);
  }

  /** Dismiss every toast, or only those raised under `key` when one is given. */
  clear(key?: string): void {
    for (const toast of this.toastsSignal()) {
      if (key === undefined || toast.key === key) {
        this.clearTimer(toast.id);
      }
    }
    this.toastsSignal.update(toasts =>
      key === undefined ? [] : toasts.filter(toast => toast.key !== key)
    );
  }

  /**
   * Custom body for toasts raised under `key`. Set by ToastTemplateDirective so a
   * feature can keep its own toast markup, the way `<p-toast key="…">` did.
   */
  registerTemplate(key: string, template: TemplateRef<unknown>): void {
    this.templatesSignal.update(templates => new Map(templates).set(key, template));
  }

  unregisterTemplate(key: string, template: TemplateRef<unknown>): void {
    const templates = this.templatesSignal();
    // A re-created host registers before the outgoing one destroys; that newer
    // template owns the key now, so leave it (and its toasts) alone.
    if (templates.get(key) !== template) {
      return;
    }
    const next = new Map(templates);
    next.delete(key);
    this.templatesSignal.set(next);
    // Toasts raised under this key would otherwise fall back to the generic card,
    // dropping the controls their custom body carried (CWS-1227 review). The body
    // is gone with its host view, so dismiss them rather than render them wrong.
    this.clear(key);
  }

  templateFor(key: string | undefined): TemplateRef<unknown> | null {
    return key ? (this.templatesSignal().get(key) ?? null) : null;
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  /** PrimeNG allowed 'warn'; callers also pass 'warning'. Anything unknown shows as info. */
  private normalizeSeverity(severity: ToastMessage['severity']): ToastSeverity {
    switch (severity) {
      case 'success':
      case 'info':
      case 'error':
        return severity;
      case 'warn':
      case 'warning':
        return 'warn';
      default:
        return 'info';
    }
  }
}
